import * as THREE from 'three';

import { Side } from '../@types/Side';
import { IWall } from '../models/abstract/Wall';
import { DEFAULT_DOOR_LENGTH, DEFAULT_PASSAGE_LENGTH, DEFAULT_RADIATOR_DEPTH, DEFAULT_WINDOW_LENGTH } from './constants';
import { getAngleFromPoints } from './vectorUtils';

const snapElementToWallCenterline = (wallModel: IWall, localPoint: THREE.Vector3, width: number): THREE.Vector3 => {
  const wallStart = new THREE.Vector3(...wallModel.startPoint);
  const wallEnd = new THREE.Vector3(...wallModel.endPoint);
  const wallDirection = new THREE.Vector3().subVectors(wallEnd, wallStart).normalize();

  const startToClick = new THREE.Vector3().subVectors(localPoint, wallStart);
  const endToClick = new THREE.Vector3().subVectors(localPoint, wallEnd);
  const projectedLengthFromStart = startToClick.dot(wallDirection);
  const projectedLengthFromEnd = endToClick.dot(wallDirection);

  if (projectedLengthFromStart < width / 2) {
    localPoint.copy(wallStart).addScaledVector(wallDirection, width / 2);
  } else if (projectedLengthFromEnd > -width / 2) {
    localPoint.copy(wallEnd).addScaledVector(wallDirection, -width / 2);
  }

  const midPoint = new THREE.Vector3((wallModel.startPoint.x + wallModel.endPoint.x) / 2, (wallModel.startPoint.y + wallModel.endPoint.y) / 2, 0);
  const angle = getAngleFromPoints(wallModel.startPoint, wallModel.endPoint);

  const rotationMatrix = new THREE.Matrix4().makeRotationZ(-angle);
  localPoint.sub(midPoint).applyMatrix4(rotationMatrix).add(midPoint);
  localPoint.y = midPoint.y;
  localPoint.sub(midPoint).applyMatrix4(rotationMatrix.makeRotationZ(angle)).add(midPoint);

  return localPoint;
};

export const snapDoorToWall = (wallModel: IWall, localPoint: THREE.Vector3, doorWidth: number = DEFAULT_DOOR_LENGTH): THREE.Vector3 =>
  snapElementToWallCenterline(wallModel, localPoint, doorWidth);

export const snapWindowToWall = (wallModel: IWall, localPoint: THREE.Vector3, windowWidth: number = DEFAULT_WINDOW_LENGTH): THREE.Vector3 =>
  snapElementToWallCenterline(wallModel, localPoint, windowWidth);

export const snapPassageToWall = (wallModel: IWall, localPoint: THREE.Vector3, passageWidth: number = DEFAULT_PASSAGE_LENGTH): THREE.Vector3 =>
  snapElementToWallCenterline(wallModel, localPoint, passageWidth);

export const snapRadiatorToWall = (
  wall: IWall,
  clickPoint: THREE.Vector3,
  width: number,
  thickness: number | undefined,
  nicheDepth: number | undefined,
  side: Side,
): THREE.Vector3 => {
  const wallStart = wall.startPoint;
  const wallEnd = wall.endPoint;
  const wallDir = new THREE.Vector3().subVectors(wallEnd, wallStart).normalize();
  const wallLen = wallStart.distanceTo(wallEnd);

  const proj = clickPoint.clone().sub(wallStart).dot(wallDir);
  const clamped = THREE.MathUtils.clamp(
    proj,
    width / 2,
    wallLen - width / 2,
  );

  const pOnCenterline = wallStart.clone().add(wallDir.multiplyScalar(clamped));
  const normal = wall.normalForSide(side);

  const totalOffset = (wall.depth / 2) + ((thickness ?? DEFAULT_RADIATOR_DEPTH) / 2) - (nicheDepth ?? 0);

  return pOnCenterline.add(normal.multiplyScalar(-totalOffset));
};
